import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { LaunchDialogService } from '@spartacus/storefront';
import { Observable, from, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { LoginLogoutActionTypes } from '../../../../../core/auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageType, isErrorGloballyHandled } from '../../../../../core/global-message';
import { HttpResponseStatus } from '../../../../../core/global-message/models/response-status.model';
import { GlobalMessageActions } from '../../../../../core/global-message/store/actions';
import { ModalCloseReason } from '../../../../../core/modal';
import { normalizeHttpError, withdrawOn } from '../../../../../core/util';
import { AppActions } from '../../../../../core/util/store/actions';
import { AsyncReturnService } from '../../../../../shared';
import { CartEntryCuttingConnector } from '../../connector/cart-entry-cutting.connector';
import { CartEntryCuttingActionTypes } from '../actions/cart-entry-cutting.action';
import { CartActions } from '../actions/index';

@Injectable()
export class CartEntryCuttingEffects {
  private contextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  addCutting$: Observable<CartActions.AddCartEntryCuttingSuccess | CartActions.AddCartEntryCuttingFail | CartActions.LoadCart> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CartEntryCuttingActionTypes.AddCartEntryCutting),
        map((action: CartActions.AddCartEntryCutting) => action.payload),
        concatMap(({ actionToken, ...payload }) =>
          this.connector.add(payload.userId, payload.cartId, payload.entry, payload.cutting).pipe(
            map(() => {
              this.asyncReturnService.resolveToken(actionToken, true);
              return new CartActions.AddCartEntryCuttingSuccess({
                ...payload,
              });
            }),
            catchError((error) => {
              this.asyncReturnService.resolveToken(actionToken, false);
              return from([
                new CartActions.AddCartEntryCuttingFail({
                  ...payload,
                  error: normalizeHttpError(error),
                }),
                new CartActions.LoadCart({
                  userId: payload.userId,
                  cartType: payload.cartType,
                  cartId: payload.cartId,
                }),
              ]);
            })
          )
        ),
        withdrawOn(this.contextChange$)
      )
    );

  handleSuccessForAddCutting$: Observable<GlobalMessageActions.AddMessage | CartActions.LoadCart> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryCuttingActionTypes.AddCartEntryCuttingSuccess),
      map((action: CartActions.AddCartEntryCuttingSuccess) => action.payload),
      concatMap((payload) => {
        this.launchDialogService.closeDialog(ModalCloseReason.CUTTING_SAVED);
        return [
          new CartActions.LoadCart({
            userId: payload.userId,
            cartType: payload.cartType,
            cartId: payload.cartId,
          }),
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.applyCartEntryCuttingSuccess_message',
              params: {
                entryNumber: +payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          }),
        ];
      })
    )
  );

  handleFailureForAddCutting$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryCuttingActionTypes.AddCartEntryCuttingFail),
      map((action: CartActions.AddCartEntryCuttingFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.applyCartEntryCuttingFailure_message',
              params: {
                entryNumber: +action.payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  removeCutting$: Observable<
    CartActions.RemoveCartEntryCuttingSuccess | CartActions.RemoveCartEntryCuttingFail | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryCuttingActionTypes.RemoveCartEntryCutting),
      map((action: CartActions.RemoveCartEntryCutting) => action.payload),
      concatMap(({ actionToken, ...payload }) =>
        this.connector.remove(payload.userId, payload.cartId, payload.entry).pipe(
          map(() => {
            this.asyncReturnService.resolveToken(actionToken, true);
            return new CartActions.RemoveCartEntryCuttingSuccess({
              ...payload,
            });
          }),
          catchError((error) => {
            this.asyncReturnService.resolveToken(actionToken, false);
            return from([
              new CartActions.RemoveCartEntryCuttingFail({
                ...payload,
                error: normalizeHttpError(error),
              }),
              new CartActions.LoadCart({
                userId: payload.userId,
                cartType: payload.cartType,
                cartId: payload.cartId,
              }),
            ]);
          })
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleSuccessForRemoveCutting$: Observable<GlobalMessageActions.AddMessage | CartActions.LoadCart> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryCuttingActionTypes.RemoveCartEntryCuttingSuccess),
      map((action: CartActions.RemoveCartEntryCuttingSuccess) => action.payload),
      concatMap((payload) => {
        this.launchDialogService.closeDialog(ModalCloseReason.CUTTING_REMOVED);

        return [
          new CartActions.LoadCart({
            userId: payload.userId,
            cartType: payload.cartType,
            cartId: payload.cartId,
          }),
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.removeCartEntryCuttingSuccess_message',
              params: {
                entryNumber: +payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          }),
        ];
      })
    )
  );

  handleFailureForRemoveCutting$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryCuttingActionTypes.RemoveCartEntryCuttingFail),
      map((action: CartActions.RemoveCartEntryCuttingFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.removeCartEntryCuttingFailure_message',
              params: {
                entryNumber: +action.payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  calculatePrice$: Observable<CartActions.CalculateCuttingPriceSuccess | CartActions.CalculateCuttingPriceFail> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartEntryCuttingActionTypes.CalculateCuttingPrice),
        map((action: CartActions.CalculateCuttingPrice) => action.payload),
        concatMap((payload) =>
          this.connector.calculatePrice(payload.userId, payload.cartId, payload.entry, payload.cutting).pipe(
            map((result) => {
              return new CartActions.CalculateCuttingPriceSuccess({
                ...payload,
                result,
              });
            }),
            catchError((error) =>
              of(
                new CartActions.CalculateCuttingPriceFail({
                  ...payload,
                  error: normalizeHttpError(error),
                })
              )
            )
          )
        ),
        withdrawOn(this.contextChange$)
      )
  );

  handleFailureForCalculatePrice$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryCuttingActionTypes.CalculateCuttingPriceFail),
      map((action: CartActions.CalculateCuttingPriceFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          if (action.payload.error?.status === HttpResponseStatus.BAD_REQUEST) {
            return new GlobalMessageActions.AddMessage({
              text: {
                raw: action.payload.error?.message,
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'cart.calculateCartEntryCuttingPriceFailure_message',
                params: {
                  entryNumber: +action.payload.entry.entryNumber + 1,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          }
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  calculateTemplate$: Observable<CartActions.CalculateCuttingTemplateSuccess | CartActions.CalculateCuttingTemplateFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CartEntryCuttingActionTypes.CalculateCuttingTemplate),
        map((action: CartActions.CalculateCuttingTemplate) => action.payload),
        concatMap((payload) =>
          this.connector.calculateTemplate(payload.userId, payload.cartId, payload.entry, payload.cuttingTemplate).pipe(
            map((result) => {
              return new CartActions.CalculateCuttingTemplateSuccess({
                ...payload,
                result,
              });
            }),
            catchError((error) =>
              of(
                new CartActions.CalculateCuttingTemplateFail({
                  ...payload,
                  error: normalizeHttpError(error),
                })
              )
            )
          )
        ),
        withdrawOn(this.contextChange$)
      )
    );

  handleFailureForCalculateTemplate$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryCuttingActionTypes.CalculateCuttingTemplateFail),
      map((action: CartActions.CalculateCuttingTemplateFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.calculateCartEntryCuttingTemplateFailure_message',
              params: {
                entryNumber: +action.payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  constructor(
    private actions$: Actions,
    private connector: CartEntryCuttingConnector,
    private launchDialogService: LaunchDialogService,
    private asyncReturnService: AsyncReturnService
  ) {}
}
